import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Alert } from 'react-bootstrap';
import { CheckCircleFill } from 'react-bootstrap-icons';
import { FormOptions } from '@data-driven-forms/react-form-renderer/dist/cjs/renderer-context';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormTemplateRenderProps from '@data-driven-forms/react-form-renderer/dist/cjs/form-template-render-props';
import { CustomThemes } from '../utils/custom-themes';
import { ContactUsRequirement } from '../components/ContactUsRequirement';
import {
  AuthLayout,
  HcpFormRenderer,
  HcpFormTemplate,
  LoginBanner,
  PublicLayout,
  ResourceAuthLayoutHeader,
  ResourceContent
} from '../components';
import { AppRootState, login, tenancyFeatureSelector } from '../redux';
import { AnyType, GeneralObject } from '../interfaces';
import {
  clientApplicationNameSelector,
  getLoginFormSchema,
  getResetPswdLoginFormSchema,
  ROUTE_PATHS,
  getConfigurationDataKey
} from '../utils';
import { useLoginUser } from '../api';
import { FORM_ERROR } from 'final-form';
import { FeatureGuard } from '../components/multi-tenant/FeatureGuard';
import { ForgotPasswordAuthLayout } from '../components/ForgotPasswordAuthLayout';
import { BAAView } from './BAA';

export const LoginView = () => {
  CustomThemes();
  const history = useHistory();
  const { hash } = useLocation();
  const dispatch = useDispatch();
  const [isReset] = useState(hash === '#reset');

  const [showBAAView, setShowBAAView] = useState(false);
  const [loginResponse, setLoginResponse] = useState({});

  const [userName, setUserName] = useState('');

  const applicationName: any = useSelector(clientApplicationNameSelector);
  const { mutate: loginUser } = useLoginUser({});
  const isLoginImage = useSelector(
    (state: AppRootState) => ((state.app?.entities?.resources as AnyType) ?? {})['login.image'] as AnyType
  );

  const onSubmit = async (values: GeneralObject) => {
    try {
      const result = await loginUser({
        userName: String(values.username),
        password: String(values.password),
        applicationName: applicationName
      });

      const resultStatus = result.loginResponse?.status?.toLowerCase();
      if (result && result.loginResponse) {
        const isError = resultStatus === 'error' || resultStatus === 'fail';
        if (result.loginResponse.isBaaAcknowledged || isError) {
          await dispatch(login(result.loginResponse));
        } else {
          setLoginResponse(result.loginResponse);
          setUserName(String(values.username));
          setShowBAAView(true);
        }
      }

      if (localStorage.getItem('rememberUsername') === 'true') {
        localStorage.setItem('username', values.username as string);
      } else {
        localStorage.removeItem('username'); // Clear if unchecked
      }
    } catch (e: any) {
      return { [FORM_ERROR]: e.message };
    }
  };

  const LoginView = () => {
    return (
      <PublicLayout fluid className='authlayout_custom_image'>
        <AuthLayout image={isLoginImage?.data?.url} banner={<LoginBanner />}>
          <ResourceAuthLayoutHeader contentKey={getConfigurationDataKey('login.title')}>
            <p>
              <ResourceContent contentKey={getConfigurationDataKey('login.body')} />
            </p>
          </ResourceAuthLayoutHeader>

          <HcpFormRenderer
            contentKey={getConfigurationDataKey('form.login')}
            FormTemplate={(props) => <LoginFormTemplate isReset={isReset} {...props} />}
            noErrorMessage
            onSubmit={onSubmit}
            schemaMethod={getLoginFormSchema}
          />
        </AuthLayout>
      </PublicLayout>
    );
  };

  const ForgotPasswordLoginView = () => {
    return (
      <PublicLayout fluid className='authlayout_custom_image'>
        <ForgotPasswordAuthLayout>
          <>
            <Alert variant='success' className='alert-custom'>
              <p className='mb-0'>
                <h2 className='success-alert-header mb-0'>
                  <CheckCircleFill className='success-alert-icon' /> <strong>Success</strong>
                </h2>
              </p>
              <hr className='mt-0 mb-0 ms-4' />
              <p className='mb-0 ms-4'>Your password has been successfully reset. Please log in below to continue.</p>
            </Alert>
          </>

          <HcpFormRenderer
            contentKey='form.login'
            FormTemplate={(props) => <LoginFormTemplate isReset={isReset} {...props} />}
            noErrorMessage
            onSubmit={onSubmit}
            schemaMethod={getResetPswdLoginFormSchema}
          />
        </ForgotPasswordAuthLayout>
      </PublicLayout>
    );
  };

  useEffect(() => {
    if (isReset) history.replace(ROUTE_PATHS.login);
  }, [isReset, history]);
  //Two different view has been rendered, as there is complete difference between the two views
  if (showBAAView) return <BAAView loginResponse={loginResponse} userName={userName} />;
  return !isReset ? <LoginView /> : <ForgotPasswordLoginView />;
};

const LoginFormTemplate = ({ isReset, ...props }: { isReset: boolean } & FormTemplateRenderProps) => {
  const { authError } = useSelector((state: AppRootState) => state.auth);
  const registrationFeature = useSelector<AnyType>(tenancyFeatureSelector('registration.disabled')) as AnyType;
  const isRegistrationDisabled = registrationFeature?.enabled ?? false;
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberUsername') === 'true');
  const [savedUsername, setSavedUsername] = useState(() => localStorage.getItem('username') || '');

  // Handle checkbox change
  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked);
    localStorage.setItem('rememberUsername', e.target.checked.toString());
  };

  return (
    <HcpFormTemplate
      {...props}
      renderControls={(formProps: FormOptions) => {
        const formState = formProps.getState();
        const { submitting, hasValidationErrors } = formState;
        // Pre-fill the username if saved in localStorage
        if (savedUsername && formState.values.username === undefined) {
          formProps.change('username', savedUsername);
        }

        return (
          <Row>
            {authError && <span className='text-danger'>{authError}</span>}
            <Col xs={12} className='text-center mb-2'>
              {!isReset && (
                <div className='d-flex align-items-center'>
                  <input type='checkbox' checked={rememberMe} onChange={handleRememberMeChange} className='me-2' />
                  <label className='m-0 text-content'>Remember my Username</label>
                </div>
              )}
            </Col>
            <Col xs={12} className='text-center my-3 text-white'>
              <Button
                className='float-start'
                type='submit'
                variant='secondary'
                disabled={submitting || hasValidationErrors}
                data-testid='cmx__login-button'>
                {submitting && !authError ? 'Submitting...' : 'Log In'}
              </Button>
              {!isReset && (
                <Link
                  to={ROUTE_PATHS.forgotPassword}
                  className='float-end text-secondary text-end fs-2  link-size'
                  data-testid='cmx__login__forgot-password'>
                  <ResourceContent contentKey={getConfigurationDataKey('general.forgot-password-link')} />
                </Link>
              )}
            </Col>
            {!isReset && (
              <Col xs={12} className='text-center'>
                <p className='float-start fs-2 text-custom-50 w-100'>
                  <p className='float-start text-content '>
                    <ResourceContent contentKey='general.register-teaser' />{' '}
                  </p>
                  {isRegistrationDisabled ? (
                    <span
                      className='float-end link-size register-disabled'
                      aria-disabled='true'
                      data-testid='cmx__login__register'>
                      Register Now
                    </span>
                  ) : (
                    <Link
                      to={ROUTE_PATHS.registration}
                      className='float-end text-secondary link-size'
                      data-testid='cmx__login__register'>
                      Register Now
                    </Link>
                  )}
                </p>
              </Col>
            )}
            {!isReset && (
              <FeatureGuard contentKey={'login_contact_us'}>
                <Col xs={12} className='text-center'>
                  <p className='fs-2'>
                    <ContactUsRequirement arrow='arrow-left' top='-95px' left='227px' />
                  </p>
                </Col>
              </FeatureGuard>
            )}
          </Row>
        );
      }}
    />
  );
};
